import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { getBillingsProjection } from '../api';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';

/* ═══════════════════════════════════════════════════════════════════════════
   Billings Projection — top-level, all-projects dashboard (warm-cream + sage).
   Answers one VP question: how much will we bill, month by month, across the
   active portfolio — and is our forecast trustworthy? Portfolio sibling of the
   per-project Forecast tab; same chart vocabulary (smoothed glide, paired bars,
   $ y-grid, dark hover tooltips). Custom inline SVG — no chart library.
   Data: GET /api/forecast/all-projects/dashboard; everything else derived here.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── money / date helpers ─────────────── */

/* Full cents EVERYWHERE except axis + tight inline labels (abbrev). */
const fmt$ = (n, { abbrev = false, signed = false, cents = true } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const abs = Math.abs(n);
  if (abbrev && abs >= 1000) {
    const m = abs >= 1_000_000;
    const div = m ? 1_000_000 : 1_000;
    const suffix = m ? 'M' : 'k';
    const value = (abs / div).toFixed(abs / div < 10 ? 1 : 0);
    return (n < 0 ? '-' : (signed ? '+' : '')) + '$' + value + suffix;
  }
  const s = abs.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  });
  if (n < 0) return '-' + s;
  if (signed && n > 0) return '+' + s;
  return s;
};

const fmtPct = (n, dp = 1, { signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const r = Math.round(n * Math.pow(10, dp)) / Math.pow(10, dp);
  const s = r.toFixed(dp) + '%';
  if (signed && r > 0) return '+' + s;
  return s;
};

const axis$ = (v) => {
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1) + 'M';
  if (v >= 1000) return '$' + Math.round(v / 1000) + 'k';
  return '$' + Math.round(v);
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtMonth = (ym) => { if (!ym) return '—'; const [y, m] = ym.split('-').map(Number); return MONTHS_SHORT[m - 1] + " '" + String(y).slice(-2); };
const fmtMonthLong = (ym) => { if (!ym) return '—'; const [y, m] = ym.split('-').map(Number); return MONTHS_LONG[m - 1] + ' ' + y; };
const monthIdx = (ym) => { const [y, m] = ym.split('-').map(Number); return y * 12 + (m - 1); };

/* Catmull-Rom → cubic Bézier smoothing (house chart smoothing) */
const smooth = (pts) => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

/* current month as 'YYYY-MM' (browser local) */
const currentMonth = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); };

/* ─────────────── icons (Icon.js vocabulary subset) ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const p = (d) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'chevron-r': return p(<path d="m9 6 6 6-6 6" />);
    case 'building': return p(<><path d="M3 21h18" /><path d="M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16" /><path d="M14 21V9h4a1 1 0 0 1 1 1v11" /><path d="M9 8h2M9 12h2M9 16h2" /></>);
    case 'chart': return p(<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />);
    case 'trend-up': return p(<><path d="m3 17 6-6 4 4 8-8" /><path d="M17 7h4v4" /></>);
    case 'trend-dn': return p(<><path d="m3 7 6 6 4-4 8 8" /><path d="M17 17h4v-4" /></>);
    case 'refresh': return p(<><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></>);
    case 'alert': return p(<><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>);
    case 'arrow-r': return p(<path d="M5 12h14M13 6l6 6-6 6" />);
    case 'table': return p(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" /></>);
    case 'grid': return p(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 14.5h18M8 4v16M13.5 4v16" /></>);
    case 'target': return p(<><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /></>);
    case 'wallet': return p(<><path d="M4 7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1" /><path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><path d="M17 13h.01" /></>);
    case 'peak': return p(<path d="m3 18 5-9 4 5 3-4 6 8" />);
    case 'lock': return p(<><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>);
    case 'funnel': return p(<path d="M3 5h18l-7 8v5l-4 2v-7L3 5Z" />);
    case 'sparkle': return p(<><path d="M12 4l1.6 4.6L18 10l-4.4 1.4L12 16l-1.6-4.6L6 10l4.4-1.4L12 4Z" /><path d="M19 4.5v3M20.5 6h-3" /></>);
    default: return null;
  }
};

/* ─────────────── cost-category palette mapping ─────────────── */

const CAT_SLOT = {
  labor: 'labor', material: 'material', materials: 'material', equipment: 'equipment',
  'general conditions': 'gc', gc: 'gc', 'general requirements': 'gc', other: 'other',
};
const PALETTE = ['labor', 'material', 'equipment', 'gc', 'other'];

/* Build category metadata from the real (free-text) category labels. Known
   labels map to their semantic --cat-* slot; anything else cycles the palette
   so colors stay distinct. */
const buildCategoryMeta = (categories, categoryBreakdown) => {
  const fromBreakdown = Object.values(categoryBreakdown || {}).flatMap((o) => Object.keys(o));
  const labels = [...new Set([...(categories || []), ...fromBreakdown])];
  const used = new Set();
  let fb = 0;
  const meta = labels.map((label) => {
    const norm = String(label).trim().toLowerCase();
    let slot = CAT_SLOT[norm];
    if (!slot || used.has(slot)) { slot = PALETTE[fb++ % PALETTE.length]; }
    used.add(slot);
    return { key: label, label, slot, token: `var(--cat-${slot})`, soft: `var(--cat-${slot}-soft)` };
  });
  meta.sort((a, b) => PALETTE.indexOf(a.slot) - PALETTE.indexOf(b.slot));
  return meta;
};

/* ─────────────── derive the chart model from the API payload ─────────────── */

const buildModel = (p) => {
  const months = p.months || [];
  const mf = p.monthlyForecast || {}, ma = p.monthlyActuals || {}, mp = p.monthlyPending || {}, cb = p.categoryBreakdown || {};
  const cur = currentMonth();
  let todayIdx = months.findIndex((m) => m >= cur);
  if (todayIdx < 0) todayIdx = months.length;
  const todayMonth = months[todayIdx] || null;

  const rows = months.map((ym, i) => {
    const projected = mf[ym] || 0;
    const isPast = i < todayIdx;
    return {
      month: ym,
      projected,
      actual: isPast ? (ma[ym] || 0) : null,
      pending: mp[ym] || 0,
      breakdown: cb[ym] || {},
    };
  });
  let cum = 0;
  rows.forEach((r, i) => { cum += (i < todayIdx ? (r.actual || 0) : r.projected); r.cumulative = cum; });

  const totalProjected = rows.reduce((a, r) => a + r.projected, 0);
  const billedToDate = rows.slice(0, todayIdx).reduce((a, r) => a + (r.actual || 0), 0);
  const projectedToDate = rows.slice(0, todayIdx).reduce((a, r) => a + r.projected, 0);
  const next3 = rows.slice(todayIdx, todayIdx + 3).reduce((a, r) => a + r.projected, 0);
  const peak = rows.reduce((b, r) => (r.projected > (b ? b.projected : -1) ? r : b), null);
  const accuracy = projectedToDate > 0 ? (billedToDate / projectedToDate) * 100 : 0;

  const byProject = (p.byProject || []).map((pr) => {
    const keys = Object.keys(pr.monthly || {}).filter((k) => (pr.monthly[k] || 0) !== 0).sort();
    const startMonth = keys[0] || null, endMonth = keys[keys.length - 1] || null;
    const span = startMonth ? (monthIdx(endMonth) - monthIdx(startMonth) + 1) : 0;
    let peakMonth = null, peakValue = 0;
    keys.forEach((k) => { if ((pr.monthly[k] || 0) > peakValue) { peakValue = pr.monthly[k]; peakMonth = k; } });
    const share = totalProjected > 0 ? (pr.total / totalProjected) * 100 : 0;
    return { ...pr, startMonth, endMonth, span, peakMonth, peakValue, share };
  });

  return {
    months, rows, axis: months, todayIdx, todayMonth,
    totalProjected, totalContract: p.totalContract || 0, billedToDate, projectedToDate, next3,
    peakMonth: peak ? { month: peak.month, value: peak.projected } : null,
    accuracy, totalRetainage: p.totalRetainage || 0, projectCount: (p.byProject || []).length,
    byProject, categories: buildCategoryMeta(p.categories, cb),
  };
};

/* horizon → rows slice (forward-anchored, keeps ~1/3 lookback for context) */
const sliceHorizon = (rows, mode, todayIdx) => {
  if (mode === 'all') return rows;
  const H = +mode;
  let start = Math.max(0, todayIdx - Math.floor(H / 3));
  let end = start + H;
  if (end > rows.length) { end = rows.length; start = Math.max(0, end - H); }
  return rows.slice(start, end);
};

/* measure container width so charts fill the card (no horizontal scroll) */
const useChartWidth = (fallback = 900) => {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => { const cw = entries[0].target.offsetWidth; if (cw > 0) setW(cw); });
    ro.observe(el);
    setW(el.offsetWidth || fallback);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, w];
};

const labelEvery = (n) => (n <= 9 ? 1 : n <= 14 ? 2 : 3);

/* ─────────────── shared dark floating tooltip ─────────────── */

const DarkTooltip = ({ x, y, width, title, rows, tw = 210 }) => {
  const left = Math.max(tw / 2 + 4, Math.min(width - tw / 2 - 4, x));
  return (
    <div style={{
      position: 'absolute', left, top: y - 12, transform: 'translate(-50%, -100%)',
      width: tw, pointerEvents: 'none', zIndex: 6, background: 'var(--text-1)', borderRadius: 9,
      boxShadow: '0 12px 28px -8px oklch(0.2 0.02 100 / 0.45)', padding: '11px 13px',
    }}>
      <div className="mono" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'color-mix(in oklch, var(--bg-page) 62%, transparent)', marginBottom: 9 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {rows.map((r) => (
          <React.Fragment key={r.label}>
            {r.divider && <div style={{ height: 1, background: 'color-mix(in oklch, var(--bg-page) 22%, transparent)', margin: '1px 0' }} />}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                {r.swatch && <span style={{ width: 8, height: 8, borderRadius: 2, background: r.swatch, flex: '0 0 auto', ...(r.dashed ? { background: 'transparent', border: `1.5px dashed ${r.swatch}`, borderRadius: 0 } : {}) }} />}
                <span style={{ fontSize: 13, color: 'color-mix(in oklch, var(--bg-page) 78%, transparent)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
              </div>
              <span className="mono" style={{ fontSize: 13.5, fontWeight: r.bold ? 700 : 500, color: r.color || 'var(--bg-page)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.015em', flex: '0 0 auto' }}>{r.value}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      <span style={{ position: 'absolute', bottom: -5, left: x - left + tw / 2, transform: 'translateX(-50%) rotate(45deg)', width: 9, height: 9, background: 'var(--text-1)', borderRadius: 1 }} />
    </div>
  );
};

const YGrid = ({ niceMax, x0, x1, TOP_PAD, CHART_H, right = false }) => (
  <g>
    {[0, 1, 2, 3, 4].map((t) => {
      const frac = t / 4;
      const y = TOP_PAD + frac * CHART_H;
      const val = niceMax * (1 - frac);
      const isBase = t === 4;
      return (
        <g key={t}>
          <line x1={x0} x2={x1} y1={y} y2={y} stroke={isBase ? 'var(--border-default)' : 'var(--border-subtle)'} strokeWidth={1} opacity={isBase ? 1 : 0.4} />
          <text x={right ? x1 + 9 : x0 - 10} y={y + 3.5} fontSize="10.5" fontFamily="var(--font-mono)" fill="var(--text-3)" textAnchor={right ? 'start' : 'end'} style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{axis$(val)}</text>
        </g>
      );
    })}
  </g>
);

/* ─────────────── card chrome ─────────────── */

const Card = ({ title, sub, right, children, pad = '20px 24px 22px', style = {} }) => (
  <section style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', padding: pad, display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
    {(title || right) && (
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          {title && <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{title}</h3>}
          {sub && <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-3)', letterSpacing: '-0.005em' }}>{sub}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

const Segmented = ({ segs, active, onChange, size = 'md' }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', padding: 3, height: size === 'sm' ? 30 : 32, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 8, width: 'fit-content', flex: '0 0 auto' }}>
    {segs.map((s) => {
      const on = s.key === active;
      return (
        <button key={s.key} type="button" onClick={() => onChange && onChange(s.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: size === 'sm' ? 22 : 24, padding: '0 13px', borderRadius: 6, fontSize: 14, fontWeight: on ? 600 : 500, color: on ? 'var(--text-1)' : 'var(--text-3)', background: on ? 'var(--bg-surface)' : 'transparent', boxShadow: on ? 'var(--shadow-xs)' : 'none', letterSpacing: '-0.005em', whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
          {s.icon && <Icon name={s.icon} size={14} />}{s.label}
        </button>
      );
    })}
  </div>
);

const Legend = ({ items }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
    {items.map((it) => (
      <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {it.hatch
          ? <span style={{ width: 12, height: 12, borderRadius: 2, border: '1px dashed var(--border-strong)', background: 'repeating-linear-gradient(45deg, transparent 0 2px, color-mix(in oklch, var(--text-4) 38%, transparent) 2px 3px)', display: 'inline-block' }} />
          : it.line
            ? <span style={{ width: 18, height: 0, borderTop: `2px ${it.dash ? 'dashed' : 'solid'} ${it.color}`, display: 'inline-block' }} />
            : <span style={{ width: 10, height: 10, borderRadius: 3, background: it.color, display: 'inline-block' }} />}
        <span style={{ fontSize: 13, color: it.muted ? 'var(--text-4)' : 'var(--text-2)' }}>{it.label}</span>
      </div>
    ))}
  </div>
);

/* ─────────────── KPI strip ─────────────── */

const ComingSoonChip = ({ label = 'Coming soon' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px 2px 7px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em', color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px dashed color-mix(in oklch, var(--accent) 35%, transparent)', whiteSpace: 'nowrap' }}>
    <span style={{ width: 5, height: 5, borderRadius: 99, background: 'var(--accent)', flex: '0 0 auto' }} />{label}
  </span>
);

const KpiTile = ({ icon, label, value, sub, tone, emphasis = false, delta, placeholder = false }) => (
  <div style={{
    flex: '1 1 0', minWidth: 168, display: 'flex', flexDirection: 'column', gap: 10, padding: '17px 19px',
    background: emphasis ? 'color-mix(in oklch, var(--accent) 7%, var(--bg-surface))' : 'var(--bg-surface)',
    border: '1px ' + (placeholder ? 'dashed var(--border-default)' : 'solid ' + (emphasis ? 'color-mix(in oklch, var(--accent) 28%, transparent)' : 'var(--border-default)')),
    borderRadius: 12, boxShadow: placeholder ? 'none' : 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
  }}>
    {emphasis && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'var(--accent)' }} />}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        width: 26, height: 26, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: placeholder ? 'var(--text-4)' : (tone || 'var(--text-3)'),
        background: placeholder ? 'var(--bg-surface-2)' : (tone ? `color-mix(in oklch, ${tone} 13%, var(--bg-surface))` : 'var(--bg-surface-2)'),
        border: '1px ' + (placeholder ? 'dashed var(--border-default)' : 'solid ' + (tone ? `color-mix(in oklch, ${tone} 22%, transparent)` : 'var(--border-subtle)')),
      }}><Icon name={icon} size={14} /></span>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
      {placeholder && <span style={{ marginLeft: 'auto' }}><ComingSoonChip /></span>}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div className="mono" style={{ fontSize: emphasis ? 23 : 21, fontWeight: 800, color: placeholder ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 16 }}>
        {delta && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2.5, color: delta.tone, fontSize: 13, fontWeight: 600 }}>
            <Icon name={delta.up ? 'trend-up' : 'trend-dn'} size={13} />
            <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{delta.text}</span>
          </span>
        )}
        {sub && <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{sub}</span>}
      </div>
    </div>
  </div>
);

const KpiStrip = ({ cd }) => {
  const accUp = cd.accuracy >= 100;
  const next3Sub = cd.todayMonth && cd.todayIdx < cd.months.length
    ? `${fmtMonth(cd.months[cd.todayIdx])}–${fmtMonth(cd.months[Math.min(cd.todayIdx + 2, cd.months.length - 1)])}` : null;
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      <KpiTile icon="wallet" label="Total Projected" tone="var(--accent-text)" value={fmt$(cd.totalProjected, { abbrev: true })} sub={`across ${cd.months.length} mo · ${cd.projectCount} projects`} />
      <KpiTile icon="trend-up" label="Next 3 Months" tone="var(--accent)" emphasis value={fmt$(cd.next3, { abbrev: true })} sub={next3Sub} />
      <KpiTile icon="peak" label="Peak Month" tone="var(--success)" value={cd.peakMonth ? fmt$(cd.peakMonth.value, { abbrev: true }) : '—'} sub={cd.peakMonth ? fmtMonthLong(cd.peakMonth.month) : null} />
      <KpiTile icon="target" label="Forecast Accuracy" tone={accUp ? 'var(--success)' : 'var(--warning)'} value={cd.projectedToDate ? fmtPct(cd.accuracy, 1) : '—'} delta={cd.projectedToDate ? { up: accUp, tone: accUp ? 'var(--success)' : 'var(--warning)', text: fmtPct(Math.abs(cd.accuracy - 100), 1) } : null} sub="billed vs projected" />
      <KpiTile icon="lock" label="Retainage Held" tone="var(--warning)" value={fmt$(cd.totalRetainage, { abbrev: true })} sub="withheld to date" />
      <KpiTile icon="building" label="Active Projects" tone="var(--text-2)" value={String(cd.projectCount)} sub="billing this window" />
      <KpiTile icon="funnel" label="Sales Pipeline Potential" placeholder value="—" sub="Fed from the sales tool" />
    </div>
  );
};

/* ─────────────── HERO — Billings over time ─────────────── */

const HeroChart = ({ months, totalContract, todayMonth, hoveredMonth, setHoveredMonth }) => {
  const [ref, width] = useChartWidth(1180);
  const HEIGHT = 360, TOP_PAD = 30, BOTTOM_PAD = 40, LEFT_AXIS = 62, RIGHT_AXIS = 64;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const x0 = LEFT_AXIS, x1 = Math.max(LEFT_AXIS + 120, width - RIGHT_AXIS);
  const plotW = x1 - x0, n = months.length;
  const COL = n ? plotW / n : plotW;
  const colX = (i) => x0 + i * COL;
  const colC = (i) => x0 + i * COL + COL / 2;
  const baseY = TOP_PAD + CHART_H;
  const BAR_W = Math.max(8, Math.min(22, COL * 0.30));
  const PAIR_GAP = Math.max(2, BAR_W * 0.18);

  const maxBar = months.reduce((mx, m) => Math.max(mx, (m.projected || 0) + (m.pending || 0), m.actual || 0), 0);
  const niceL = maxBar * 1.15 || 100_000;
  const yL = (v) => TOP_PAD + (1 - v / niceL) * CHART_H;
  const niceR = totalContract * 1.06 || 1;
  const yR = (v) => TOP_PAD + (1 - v / niceR) * CHART_H;
  const contractY = yR(totalContract);

  const cumPts = months.map((m, i) => ({ x: colC(i), y: yR(m.cumulative) }));
  const splitAt = todayMonth ? months.filter((m) => m.month < todayMonth).length : n;
  const solidPts = cumPts.slice(0, Math.max(2, splitAt + 1));
  const dashPts = cumPts.slice(Math.max(0, splitAt));
  const solidLine = solidPts.length >= 2 ? smooth(solidPts) : '';
  const dashLine = dashPts.length >= 2 ? smooth(dashPts) : '';
  const dividerX = splitAt > 0 && splitAt < n ? colX(splitAt) : null;
  const ev = labelEvery(n);

  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width={width} height={HEIGHT} style={{ display: 'block' }} onMouseLeave={() => setHoveredMonth(null)}>
        <defs>
          <linearGradient id="bpHeroProj" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" /><stop offset="100%" stopColor="color-mix(in oklch, var(--accent) 42%, var(--accent-soft))" /></linearGradient>
          <linearGradient id="bpHeroAct" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--success)" /><stop offset="100%" stopColor="color-mix(in oklch, var(--success) 45%, var(--success-soft))" /></linearGradient>
          <linearGradient id="bpHeroPend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--warning)" /><stop offset="100%" stopColor="color-mix(in oklch, var(--warning) 50%, var(--warning-soft))" /></linearGradient>
          <filter id="bpHeroShadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="oklch(0.2 0.02 135)" floodOpacity="0.12" /></filter>
          <filter id="bpHeroDot" x="-150%" y="-150%" width="400%" height="400%"><feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="var(--text-1)" floodOpacity="0.35" /></filter>
          <pattern id="bpHeroHatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="7" stroke="var(--text-4)" strokeWidth="1.1" opacity="0.3" /></pattern>
        </defs>

        <YGrid niceMax={niceL} x0={x0} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />
        <YGrid niceMax={niceR} x0={x0} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} right />

        {dividerX != null && (
          <>
            <rect x={dividerX} y={TOP_PAD} width={x1 - dividerX} height={CHART_H} fill="color-mix(in oklch, var(--accent) 3.5%, transparent)" />
            <line x1={dividerX} x2={dividerX} y1={TOP_PAD - 6} y2={baseY} stroke="var(--text-3)" strokeWidth={1.25} strokeDasharray="4 4" opacity={0.55} />
            <g transform={`translate(${dividerX}, ${TOP_PAD - 6})`}>
              <rect x={-22} y={-15} width={44} height={16} rx={8} fill="var(--text-1)" />
              <text x={0} y={-3.5} fontSize="9.5" fontWeight="700" fontFamily="var(--font-sans)" fill="var(--bg-surface)" textAnchor="middle" style={{ letterSpacing: '0.04em' }}>TODAY</text>
            </g>
          </>
        )}

        {/* reserved ghost band — Sales pipeline upside (coming soon) */}
        {dividerX != null && (x1 - dividerX) > 170 && (
          <g>
            <rect x={dividerX + 6} y={TOP_PAD + 3} width={x1 - dividerX - 12} height={26} rx={7} fill="color-mix(in oklch, var(--text-4) 5%, transparent)" />
            <rect x={dividerX + 6} y={TOP_PAD + 3} width={x1 - dividerX - 12} height={26} rx={7} fill="url(#bpHeroHatch)" stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="5 4" />
            <text x={dividerX + 6 + (x1 - dividerX - 12) / 2} y={TOP_PAD + 19.5} fontSize="10.5" fontWeight="600" fontFamily="var(--font-sans)" fill="var(--text-3)" textAnchor="middle" style={{ letterSpacing: '0.01em' }}>Sales pipeline upside — coming soon</text>
          </g>
        )}

        <line x1={x0} x2={x1} y1={contractY} y2={contractY} stroke="var(--accent)" strokeWidth={1.25} strokeDasharray="2 4" opacity={0.45} />

        {hoveredIndex >= 0 && <rect x={colX(hoveredIndex)} y={TOP_PAD} width={COL} height={CHART_H} fill="color-mix(in oklch, var(--accent) 5%, transparent)" rx={5} />}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          const dim = hoveredMonth && !isHovered;
          const elapsed = m.actual != null;
          const cx = colC(i);
          const groupW = elapsed ? BAR_W * 2 + PAIR_GAP : BAR_W;
          let cur = cx - groupW / 2;
          const projX = cur; cur += BAR_W + (elapsed ? PAIR_GAP : 0);
          const actX = cur;
          const projY = yL(m.projected), projH = baseY - projY;
          const pendH = m.pending ? (baseY - yL(m.pending)) : 0;
          const pendY = projY - pendH;
          return (
            <g key={m.month} onMouseEnter={() => setHoveredMonth(m.month)}>
              <rect x={colX(i)} y={TOP_PAD} width={COL} height={CHART_H + BOTTOM_PAD} fill="transparent" />
              <rect x={projX} y={projY} width={BAR_W} height={Math.max(2, projH)} rx={Math.min(4, BAR_W / 2)} fill="url(#bpHeroProj)" opacity={dim ? 0.5 : 1} filter={isHovered ? 'url(#bpHeroShadow)' : 'none'} />
              {m.pending > 0 && <rect x={projX} y={pendY} width={BAR_W} height={Math.max(2, pendH)} rx={Math.min(4, BAR_W / 2)} fill="url(#bpHeroPend)" opacity={dim ? 0.5 : 1} />}
              {elapsed && <rect x={actX} y={yL(m.actual)} width={BAR_W} height={Math.max(2, baseY - yL(m.actual))} rx={Math.min(4, BAR_W / 2)} fill="url(#bpHeroAct)" opacity={dim ? 0.5 : 1} filter={isHovered ? 'url(#bpHeroShadow)' : 'none'} />}
              {(i % ev === 0 || i === n - 1) && (
                <text x={cx} y={HEIGHT - 14} fontSize="10" fontFamily="var(--font-sans)" fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'} fontWeight={isHovered ? 700 : 500} textAnchor="middle" style={{ letterSpacing: '-0.005em' }}>{fmtMonth(m.month)}</text>
              )}
            </g>
          );
        })}

        {solidLine && <path d={solidLine} fill="none" stroke="var(--text-1)" strokeWidth={2.25} strokeLinejoin="round" strokeLinecap="round" opacity={0.82} />}
        {dashLine && <path d={dashLine} fill="none" stroke="var(--text-1)" strokeWidth={2.25} strokeLinejoin="round" strokeLinecap="round" strokeDasharray="5 4" opacity={0.5} />}
        {months.map((m, i) => (m.month === hoveredMonth ? <circle key={m.month} cx={cumPts[i].x} cy={cumPts[i].y} r={5} fill="var(--bg-surface)" stroke="var(--text-1)" strokeWidth={2.5} filter="url(#bpHeroDot)" /> : null))}
      </svg>

      <div className="mono" style={{ position: 'absolute', right: RIGHT_AXIS + 4, top: yR(totalContract) - 9, fontSize: 11.5, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', padding: '1px 7px', borderRadius: 999, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}>Contract {fmt$(totalContract, { abbrev: true })}</div>

      {hovered && (
        <DarkTooltip x={colC(hoveredIndex)} y={Math.min(yL(Math.max(hovered.projected + hovered.pending, hovered.actual || 0)), cumPts[hoveredIndex].y)} width={width} title={fmtMonthLong(hovered.month)}
          rows={[
            { label: 'Projected', value: fmt$(hovered.projected), swatch: 'var(--accent)' },
            ...(hovered.actual != null ? [{ label: 'Actual billed', value: fmt$(hovered.actual), swatch: 'var(--success)' }] : []),
            ...(hovered.pending > 0 ? [{ label: 'Pending review', value: fmt$(hovered.pending), swatch: 'var(--warning)' }] : []),
            ...(hovered.actual != null && hovered.projected > 0
              ? [{ label: 'Variance', bold: true, divider: true, value: fmtPct(((hovered.actual - hovered.projected) / hovered.projected) * 100, 1, { signed: true }), color: hovered.actual >= hovered.projected ? 'var(--success)' : 'var(--warning)' }]
              : [{ label: 'Cumulative', value: fmt$(hovered.cumulative), bold: true, divider: true }]),
          ]} />
      )}
    </div>
  );
};

/* ─────────────── BY CATEGORY — stacked monthly bars ─────────────── */

const CategoryChart = ({ months, cats, todayMonth, hoveredMonth, setHoveredMonth }) => {
  const [ref, width] = useChartWidth(680);
  const HEIGHT = 300, TOP_PAD = 24, BOTTOM_PAD = 36, LEFT_AXIS = 58, RIGHT_PAD = 14;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const x0 = LEFT_AXIS, x1 = Math.max(LEFT_AXIS + 80, width - RIGHT_PAD);
  const plotW = x1 - x0, n = months.length, COL = n ? plotW / n : plotW;
  const colX = (i) => x0 + i * COL, colC = (i) => x0 + i * COL + COL / 2;
  const baseY = TOP_PAD + CHART_H;
  const BAR_W = Math.max(10, Math.min(30, COL * 0.56));
  const maxVal = months.reduce((mx, m) => Math.max(mx, m.projected), 0);
  const niceMax = maxVal * 1.15 || 100_000;
  const present = cats.filter((c) => months.some((m) => (m.breakdown[c.key] || 0) > 0));
  const ev = labelEvery(n);
  const splitAt = todayMonth ? months.filter((m) => m.month < todayMonth).length : n;
  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width={width} height={HEIGHT} style={{ display: 'block' }} onMouseLeave={() => setHoveredMonth(null)}>
        <defs>
          {cats.map((c) => (<linearGradient key={c.key} id={`bpCat-${c.slot}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c.token} /><stop offset="100%" stopColor={c.soft} /></linearGradient>))}
          <filter id="bpCatShadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="oklch(0.2 0.02 135)" floodOpacity="0.1" /></filter>
        </defs>

        <YGrid niceMax={niceMax} x0={x0} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />
        {splitAt > 0 && splitAt < n && <line x1={colX(splitAt)} x2={colX(splitAt)} y1={TOP_PAD} y2={baseY} stroke="var(--text-3)" strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />}
        {hoveredIndex >= 0 && <rect x={colX(hoveredIndex)} y={TOP_PAD} width={COL} height={CHART_H} fill="color-mix(in oklch, var(--accent) 4%, transparent)" rx={5} />}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          const dim = hoveredMonth && !isHovered;
          const cx = colC(i), bx = cx - BAR_W / 2;
          let cursorY = baseY;
          const segs = present.map((c) => {
            const val = m.breakdown[c.key] || 0;
            const h = (val / niceMax) * CHART_H;
            const y = cursorY - h; cursorY = y;
            return { c, val, y, h };
          }).filter((s) => s.val > 0);
          return (
            <g key={m.month} onMouseEnter={() => setHoveredMonth(m.month)}>
              <rect x={colX(i)} y={TOP_PAD} width={COL} height={CHART_H + BOTTOM_PAD} fill="transparent" />
              <g filter={isHovered ? 'url(#bpCatShadow)' : 'none'} opacity={dim ? 0.5 : 1}>
                {segs.map((s) => (<rect key={s.c.key} x={bx} y={s.y} width={BAR_W} height={Math.max(1.5, s.h)} rx={3} fill={`url(#bpCat-${s.c.slot})`} />))}
              </g>
              {(i % ev === 0 || i === n - 1) && (<text x={cx} y={HEIGHT - 12} fontSize="10" fontFamily="var(--font-sans)" fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'} fontWeight={isHovered ? 700 : 500} textAnchor="middle">{fmtMonth(m.month)}</text>)}
            </g>
          );
        })}
      </svg>

      {hovered && (
        <DarkTooltip x={colC(hoveredIndex)} y={TOP_PAD + (1 - hovered.projected / niceMax) * CHART_H} width={width} title={fmtMonthLong(hovered.month)}
          rows={[
            ...present.filter((c) => (hovered.breakdown[c.key] || 0) > 0).map((c) => ({ label: c.label, value: fmt$(hovered.breakdown[c.key]), swatch: c.token })),
            { label: 'Total', value: fmt$(hovered.projected), bold: true, divider: true },
          ]} />
      )}
    </div>
  );
};

/* ─────────────── S-CURVE — cumulative burn-up toward contract ─────────────── */

const SCurveChart = ({ months, totalContract, todayMonth, hoveredMonth, setHoveredMonth }) => {
  const [ref, width] = useChartWidth(680);
  const HEIGHT = 300, TOP_PAD = 24, BOTTOM_PAD = 36, LEFT_AXIS = 58, RIGHT_PAD = 70;
  const CHART_H = HEIGHT - TOP_PAD - BOTTOM_PAD;
  const x0 = LEFT_AXIS, x1 = Math.max(LEFT_AXIS + 80, width - RIGHT_PAD);
  const plotW = x1 - x0, n = months.length;
  const baseY = TOP_PAD + CHART_H;
  const xFor = (i) => x0 + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const niceMax = totalContract * 1.06 || 1;
  const yFor = (v) => TOP_PAD + (1 - v / niceMax) * CHART_H;
  const contractY = yFor(totalContract);
  const halfHit = n > 1 ? plotW / (n - 1) / 2 : plotW / 2;

  const pts = months.map((m, i) => ({ x: xFor(i), y: yFor(m.cumulative) }));
  const splitAt = todayMonth ? months.filter((m) => m.month < todayMonth).length : n - 1;
  const solidPts = pts.slice(0, Math.max(2, splitAt + 1));
  const dashPts = pts.slice(Math.max(0, splitAt));
  const solidLine = solidPts.length >= 2 ? smooth(solidPts) : '';
  const dashLine = dashPts.length >= 2 ? smooth(dashPts) : '';
  const areaSrc = solidPts.length >= 2 ? solidPts : pts;
  const area = areaSrc.length >= 2 ? `${smooth(areaSrc)} L ${areaSrc[areaSrc.length - 1].x} ${baseY} L ${areaSrc[0].x} ${baseY} Z` : '';
  const ev = labelEvery(n);
  const dividerX = splitAt > 0 && splitAt < n ? xFor(splitAt) : null;
  const hoveredIndex = months.findIndex((m) => m.month === hoveredMonth);
  const hovered = months[hoveredIndex];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width={width} height={HEIGHT} style={{ display: 'block' }} onMouseLeave={() => setHoveredMonth(null)}>
        <defs>
          <linearGradient id="bpScArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity="0.14" /><stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" /></linearGradient>
          <filter id="bpScDot" x="-150%" y="-150%" width="400%" height="400%"><feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="var(--accent)" floodOpacity="0.5" /></filter>
        </defs>

        <YGrid niceMax={niceMax} x0={x0} x1={x1} TOP_PAD={TOP_PAD} CHART_H={CHART_H} />

        <line x1={x0} x2={x1} y1={contractY} y2={contractY} stroke="var(--success)" strokeWidth={1.5} strokeDasharray="5 4" opacity={0.6} />
        <g>
          <rect x={x1 + 6} y={contractY - 9} width={60} height={18} rx={9} fill="var(--success-soft)" />
          <text x={x1 + 36} y={contractY + 3.5} fontSize="10" fontWeight="600" fontFamily="var(--font-sans)" fill="var(--success)" textAnchor="middle">Contract</text>
        </g>

        {dividerX != null && (
          <>
            <line x1={dividerX} x2={dividerX} y1={TOP_PAD} y2={baseY} stroke="var(--text-3)" strokeWidth={1} strokeDasharray="3 3" opacity={0.45} />
            <text x={dividerX} y={TOP_PAD - 6} fontSize="9" fontWeight="700" fontFamily="var(--font-sans)" fill="var(--text-3)" textAnchor="middle" style={{ letterSpacing: '0.05em' }}>TODAY</text>
          </>
        )}

        {area && <path d={area} fill="url(#bpScArea)" />}
        {solidLine && <path d={solidLine} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />}
        {dashLine && <path d={dashLine} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6 4" opacity={0.7} />}
        {hoveredIndex >= 0 && <line x1={pts[hoveredIndex].x} x2={pts[hoveredIndex].x} y1={TOP_PAD} y2={baseY} stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="4 4" opacity={0.14} />}

        {months.map((m, i) => {
          const isHovered = m.month === hoveredMonth;
          return (
            <g key={m.month} onMouseEnter={() => setHoveredMonth(m.month)}>
              <rect x={pts[i].x - halfHit} y={TOP_PAD} width={halfHit * 2} height={CHART_H + BOTTOM_PAD} fill="transparent" />
              <circle cx={pts[i].x} cy={pts[i].y} r={isHovered ? 6 : 3.5} fill="var(--bg-surface)" stroke="var(--accent)" strokeWidth={isHovered ? 3 : 2} filter={isHovered ? 'url(#bpScDot)' : 'none'} style={{ transition: 'r 120ms ease' }} />
              {(i % ev === 0 || i === n - 1) && (<text x={pts[i].x} y={HEIGHT - 12} fontSize="10" fontFamily="var(--font-sans)" fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'} fontWeight={isHovered ? 700 : 500} textAnchor="middle">{fmtMonth(m.month)}</text>)}
            </g>
          );
        })}
      </svg>

      {hovered && (
        <DarkTooltip x={pts[hoveredIndex].x} y={pts[hoveredIndex].y} width={width} title={fmtMonthLong(hovered.month)}
          rows={[
            { label: 'Cumulative billed', value: fmt$(hovered.cumulative), swatch: 'var(--accent)' },
            { label: '% of contract', value: totalContract > 0 ? fmtPct((hovered.cumulative / totalContract) * 100, 1) : '—', color: 'var(--bg-page)' },
            { label: 'Remaining', value: fmt$(Math.max(0, totalContract - hovered.cumulative)), bold: true, divider: true },
          ]} />
      )}
    </div>
  );
};

/* ─────────────── per-project breakdown — table ─────────────── */

const ProjectTable = ({ rows, allCount, search, totalProjected, onOpenProject }) => {
  const [hover, setHover] = useState(null);
  const maxShare = rows.reduce((mx, r) => Math.max(mx, r.share), 0) || 1;
  // While the page's search hides projects, the footer adds up the ones shown (as every page does).
  const narrowed = rows.length < allCount;
  const shownTotal = rows.reduce((s, r) => s + (r.total || 0), 0);
  const shownShare = rows.reduce((s, r) => s + (r.share || 0), 0);
  const Th = ({ children, align = 'left', w }) => (<th style={{ textAlign: align, padding: '0 16px 10px', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap', width: w }}>{children}</th>);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
            <Th>Project</Th><Th>Span</Th><Th>Client · PM</Th><Th align="right">Peak month</Th><Th align="right">Projected</Th><Th align="right" w={210}>Share of portfolio</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && search && search.trim() && (
            <tr><td colSpan={6} style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)} onClick={() => onOpenProject && onOpenProject(r.slug || r.id)}
              style={{ borderBottom: '1px solid var(--border-subtle)', cursor: onOpenProject ? 'pointer' : 'default', background: hover === r.id ? 'var(--bg-hover)' : 'transparent', transition: 'background 90ms ease' }}>
              <td style={{ padding: '13px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, flex: '0 0 auto', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="building" size={15} /></span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.006em', whiteSpace: 'nowrap' }}>{r.name}</span>
                    {r.number && <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{r.number}</span>}
                  </div>
                </div>
              </td>
              <td style={{ padding: '13px 16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="mono" style={{ fontSize: 14, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.startMonth ? `${fmtMonth(r.startMonth)} – ${fmtMonth(r.endMonth)}` : '—'}</span>
                  <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{r.span} mo</span>
                </div>
              </td>
              <td style={{ padding: '13px 16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{r.client || '—'}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{r.pm || '—'}</span>
                </div>
              </td>
              <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                  <span className="mono" style={{ fontSize: 14, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmt$(r.peakValue, { abbrev: true })}</span>
                  <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{fmtMonth(r.peakMonth)}</span>
                </div>
              </td>
              <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                <span className="mono" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{fmt$(r.total)}</span>
              </td>
              <td style={{ padding: '13px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                  <div style={{ flex: '1 1 auto', maxWidth: 120, height: 6, borderRadius: 999, background: 'var(--bg-surface-2)', overflow: 'hidden' }}>
                    <div style={{ width: Math.max(2, (r.share / maxShare) * 100) + '%', height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--accent), color-mix(in oklch, var(--accent) 55%, var(--accent-soft)))' }} />
                  </div>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', minWidth: 46, textAlign: 'right' }}>{fmtPct(r.share, 1)}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ padding: '14px 16px 2px', fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{narrowed ? `Total · ${rows.length} of ${allCount} projects` : 'Portfolio total'}</td>
            <td style={{ padding: '14px 16px 2px', textAlign: 'right' }}><span className="mono" style={{ fontSize: 15.5, fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmt$(narrowed ? shownTotal : totalProjected)}</span></td>
            <td style={{ padding: '14px 16px 2px', textAlign: 'right' }}><span className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{narrowed ? fmtPct(shownShare, 1) : '100.0%'}</span></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

/* ─────────────── per-project breakdown — heatmap ─────────────── */

const Heatmap = ({ rows, axis, todayIdx, onOpenProject }) => {
  const [ref, width] = useChartWidth(1100);
  const [hover, setHover] = useState(null);
  const GUTTER = 196, TOP = 30, ROW_H = 34, GAP = 3, RIGHT = 8, BOT = 6;
  const n = axis.length;
  const plotW = Math.max(120, width - GUTTER - RIGHT);
  const CELL_W = n ? (plotW - (n - 1) * GAP) / n : plotW;
  const cellX = (ci) => GUTTER + ci * (CELL_W + GAP);
  const rowY = (ri) => TOP + ri * (ROW_H + GAP);
  const HEIGHT = TOP + rows.length * (ROW_H + GAP) + BOT;
  const maxCell = rows.reduce((mx, r) => Math.max(mx, ...axis.map((ym) => r.monthly[ym] || 0)), 0) || 1;
  const ev = n <= 9 ? 1 : n <= 14 ? 2 : 3;
  // rows[hover.ri] can be gone: the page's search narrows the rows under a resting pointer.
  const hovered = hover && rows[hover.ri] ? { r: rows[hover.ri], ym: axis[hover.ci], v: rows[hover.ri].monthly[axis[hover.ci]] || 0 } : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width={width} height={HEIGHT} style={{ display: 'block' }} onMouseLeave={() => setHover(null)}>
        {axis.map((ym, ci) => ((ci % ev === 0 || ci === n - 1) && (
          <text key={ym} x={cellX(ci) + CELL_W / 2} y={TOP - 12} fontSize="10" fontFamily="var(--font-sans)" fill={hover && hover.ci === ci ? 'var(--text-1)' : 'var(--text-3)'} fontWeight={hover && hover.ci === ci ? 700 : 500} textAnchor="middle">{fmtMonth(ym)}</text>
        )))}
        {todayIdx > 0 && todayIdx < n && <line x1={cellX(todayIdx) - GAP / 2} x2={cellX(todayIdx) - GAP / 2} y1={TOP - 6} y2={HEIGHT - BOT} stroke="var(--text-3)" strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />}

        {rows.map((r, ri) => (
          <g key={r.id}>
            <text x={0} y={rowY(ri) + ROW_H / 2 + 4} fontSize="12" fontFamily="var(--font-sans)" fontWeight={500} fill={hover && hover.ri === ri ? 'var(--text-1)' : 'var(--text-2)'}>{r.name.length > 26 ? r.name.slice(0, 25) + '…' : r.name}</text>
            {r.number && <text x={0} y={rowY(ri) + ROW_H / 2 + 17} fontSize="9.5" fontFamily="var(--font-mono)" fill="var(--text-4)" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.number}</text>}
            {axis.map((ym, ci) => {
              const v = r.monthly[ym] || 0;
              const t = v > 0 ? 0.12 + 0.88 * Math.pow(v / maxCell, 0.7) : 0;
              const isHover = hover && hover.ri === ri && hover.ci === ci;
              return (
                <rect key={ym} x={cellX(ci)} y={rowY(ri)} width={CELL_W} height={ROW_H} rx={4}
                  onMouseEnter={() => setHover({ ri, ci })} onClick={() => v > 0 && onOpenProject && onOpenProject(r.slug || r.id)}
                  fill={v > 0 ? `color-mix(in oklch, var(--accent) ${(t * 100).toFixed(0)}%, var(--bg-surface))` : 'var(--bg-surface-2)'}
                  stroke={isHover ? 'var(--accent)' : 'var(--border-subtle)'} strokeWidth={isHover ? 2 : 1} style={{ cursor: v > 0 && onOpenProject ? 'pointer' : 'default' }} />
              );
            })}
          </g>
        ))}
      </svg>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingLeft: GUTTER, fontSize: 12.5, color: 'var(--text-3)' }}>
        <span>Lower</span>
        <div style={{ display: 'flex', gap: 3 }}>
          {[0.12, 0.34, 0.56, 0.78, 1].map((t) => (<span key={t} style={{ width: 22, height: 11, borderRadius: 3, border: '1px solid var(--border-subtle)', background: `color-mix(in oklch, var(--accent) ${(t * 100).toFixed(0)}%, var(--bg-surface))` }} />))}
        </div>
        <span>Higher monthly projected</span>
      </div>

      {hovered && hovered.v > 0 && (
        <DarkTooltip x={cellX(hover.ci) + CELL_W / 2} y={rowY(hover.ri)} width={width} tw={232} title={hovered.r.name}
          rows={[
            { label: fmtMonthLong(hovered.ym), value: fmt$(hovered.v), swatch: 'var(--accent)' },
            { label: '% of project', value: hovered.r.total > 0 ? fmtPct((hovered.v / hovered.r.total) * 100, 1) : '—', color: 'var(--bg-page)' },
            { label: axis.indexOf(hovered.ym) >= todayIdx ? 'Projected' : 'Actual / elapsed', value: hovered.r.number || '', bold: true, divider: true, color: 'color-mix(in oklch, var(--bg-page) 70%, transparent)' },
          ]} />
      )}
    </div>
  );
};

/* ─────────────── category chips ─────────────── */

const CategoryChips = ({ rows, cats }) => {
  const totals = cats.map((c) => ({ c, total: rows.reduce((a, m) => a + (m.breakdown[c.key] || 0), 0) })).filter((t) => t.total > 0);
  if (totals.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      {totals.map(({ c, total }) => (
        <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 9px 4px 8px', borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: c.token, flex: '0 0 auto' }} />
          <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 500 }}>{c.label}</span>
          <span className="mono" style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt$(total, { abbrev: true })}</span>
        </div>
      ))}
    </div>
  );
};

/* ─────────────── sales pipeline — coming-soon card ─────────────── */

const PipelineComingSoon = () => {
  const GHOST = 'var(--border-strong)';
  const subLabel = { fontSize: 12.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 12 };
  return (
    <Card title="Sales pipeline potential" sub="Win-probability-weighted billings from prospective, not-yet-won projects — fed from your sales tool"
      right={(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px dashed color-mix(in oklch, var(--accent) 35%, transparent)' }}><Icon name="sparkle" size={13} /> Coming soon</span>)}>
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 30, opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
          <div>
            <div style={subLabel}>Weighted pipeline by month</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9, height: 148, borderBottom: '1px dashed var(--border-default)', padding: '0 2px' }}>
              {[26, 34, 44, 52, 60, 68, 63, 74, 80, 88, 82, 90].map((h, i) => (<div key={i} style={{ flex: 1, height: h + '%', borderRadius: '5px 5px 0 0', border: '1px dashed ' + GHOST, borderBottom: 'none', background: 'repeating-linear-gradient(45deg, transparent 0 3px, color-mix(in oklch, var(--text-4) 20%, transparent) 3px 4px)' }} />))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, padding: '0 2px' }}>{[0, 1, 2, 3, 4].map((i) => <span key={i} style={{ width: 34, height: 7, borderRadius: 4, background: 'var(--bg-surface-2)' }} />)}</div>
          </div>
          <div>
            <div style={subLabel}>Top opportunities</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, border: '1px dashed ' + GHOST, flex: '0 0 auto' }} />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 }}>
                    <span style={{ height: 9, width: (72 - i * 9) + '%', borderRadius: 4, background: 'var(--bg-surface-2)' }} />
                    <span style={{ height: 7, width: '34%', borderRadius: 4, background: 'var(--bg-surface-2)' }} />
                  </span>
                  <span style={{ width: 52, height: 14, borderRadius: 7, background: 'var(--bg-surface-2)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '20px 26px', borderRadius: 14, maxWidth: 460, background: 'color-mix(in oklch, var(--bg-surface) 85%, transparent)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-md)', backdropFilter: 'blur(2px)' }}>
            <span style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name="sparkle" size={20} /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Pipeline upside is coming soon</div>
              <div style={{ fontSize: 14, color: 'var(--text-3)', lineHeight: 1.6 }}>Once your sales tool is connected, win-probability-weighted billings from prospective projects layer in here — and as the upside band on the hero chart.</div>
            </div>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)', display: 'inline-flex', gap: 9, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span>salesPotentialByMonth</span><span style={{ opacity: 0.5 }}>·</span><span>pipelineCount</span><span style={{ opacity: 0.5 }}>·</span><span>totalWeightedPipeline</span>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

/* ─────────────── loading / empty / error ─────────────── */

const Sk = ({ w, h, r = 6, style }) => (<span style={{ display: 'inline-block', width: w, height: h, borderRadius: r, background: 'linear-gradient(90deg, var(--bg-surface-2) 0%, var(--bg-hover) 50%, var(--bg-surface-2) 100%)', backgroundSize: '200% 100%', animation: 'bp-shimmer 1.3s ease-in-out infinite', ...style }} />);

const LoadingSkeleton = () => (
  <div className="bp-page">
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ flex: '1 1 0', minWidth: 168, padding: '17px 19px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Sk w="60%" h={12} /><Sk w="80%" h={22} /><Sk w="45%" h={10} />
        </div>
      ))}
    </div>
    <Card title="Billings over time" sub="Rolling up across active projects…">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 300, padding: '0 8px' }}>
        {Array.from({ length: 16 }).map((_, i) => <Sk key={i} w="100%" h={`${30 + (i * 37 % 60)}%`} style={{ flex: 1 }} />)}
      </div>
    </Card>
  </div>
);

const EmptyState = () => (
  <Card style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 320, gap: 16, padding: '56px 24px' }}>
    <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name="chart" size={24} sw={1.8} /></div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
      <h3 style={{ margin: 0, fontSize: 17.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>No active projects to forecast</h3>
      <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.7 }}>Portfolio billings projection is rolled up from each project’s schedule and SOV. Once active projects have dated tasks and allocations, their forecasts appear here.</p>
    </div>
    <a href="/projects" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', textDecoration: 'none' }}>View all projects <Icon name="arrow-r" size={13} /></a>
  </Card>
);

const ErrorState = ({ onRetry }) => (
  <section style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'inset 3px 0 0 var(--danger), var(--shadow-sm)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px' }}>
      <div style={{ color: 'var(--danger)', marginTop: 1, flex: '0 0 auto' }}><Icon name="alert" size={18} sw={1.8} /></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>Couldn’t load the portfolio forecast</div>
        <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55 }}>Something went wrong rolling up billings across active projects.{' '}
          <button onClick={onRetry} style={{ color: 'var(--accent-text)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Try again</button>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────── page ─────────────── */

const BillingsProjectionPage = ({ onOpenProject }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [horizon, setHorizon] = useState('18');
  const [hovered, setHovered] = useState(null);
  const [breakdown, setBreakdown] = useState('table');

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const result = await getBillingsProjection();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load billings projection');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const cd = useMemo(() => (data ? buildModel(data) : null), [data]);
  // The top bar's search (lib/pageSearch) narrows the per-project breakdown: project, number, client, PM.
  // The tiles and charts are the portfolio's monthly totals from the API, so they stay whole.
  const search = usePageSearch('Search billings projection…');
  const projectRows = useMemo(
    () => (cd ? cd.byProject.filter((r) => matchesSearch(search, r.name, r.number, r.client, r.pm)) : []),
    [cd, search],
  );

  return (
    <div className="bp-page">
      <style>{`
        @keyframes bp-spin { to { transform: rotate(360deg); } }
        @keyframes bp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .bp-page { display: flex; flex-direction: column; gap: 16px; }
        .bp-twoup { display: grid; grid-template-columns: minmax(0, 1.06fr) minmax(0, 0.94fr); gap: 16px; }
        @media (max-width: 1120px) { .bp-twoup { grid-template-columns: 1fr; } }
        .bp-ghost:hover { background: var(--bg-hover); color: var(--text-1); }
      `}</style>

      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState onRetry={() => loadData()} />
      ) : !cd || cd.months.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* toolbar — title lives in the global AppHeader; this gives context + Refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, color: 'var(--text-3)' }}>
              Portfolio billings across <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{cd.projectCount}</span> active project{cd.projectCount === 1 ? '' : 's'}
            </div>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => loadData(true)} className="bp-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
              <span style={{ display: 'inline-flex', animation: refreshing ? 'bp-spin 0.7s linear infinite' : 'none' }}><Icon name="refresh" size={14} /></span> Refresh
            </button>
          </div>

          {/* 1 — KPI strip */}
          <KpiStrip cd={cd} />

          {/* 2 — hero */}
          <Card title="Billings over time" sub="Monthly projected vs actual, with a cumulative glide toward contract value"
            right={<Segmented active={horizon} onChange={setHorizon} segs={[{ key: '6', label: '6 mo' }, { key: '12', label: '12 mo' }, { key: '18', label: '18 mo' }, { key: 'all', label: 'All' }]} />}>
            <div style={{ marginBottom: 12 }}>
              <Legend items={[
                { label: 'Projected', color: 'var(--accent)' },
                { label: 'Actual billed', color: 'var(--success)' },
                { label: 'Pending review', color: 'var(--warning)' },
                { label: 'Cumulative billings', line: true, color: 'var(--text-1)' },
                { label: 'Contract value', line: true, dash: true, color: 'var(--accent)' },
                { label: 'Sales pipeline (soon)', hatch: true, muted: true },
              ]} />
            </div>
            <HeroChart months={sliceHorizon(cd.rows, horizon, cd.todayIdx)} totalContract={cd.totalContract} todayMonth={cd.todayMonth} hoveredMonth={hovered} setHoveredMonth={setHovered} />
          </Card>

          {/* 3 — two-up */}
          <div className="bp-twoup">
            <Card title="Forecast by cost category" sub="Stacked monthly billings across the active portfolio">
              <div style={{ marginBottom: 14 }}><CategoryChips rows={cd.rows} cats={cd.categories} /></div>
              <CategoryChart months={sliceHorizon(cd.rows, horizon === 'all' ? '18' : horizon, cd.todayIdx)} cats={cd.categories} todayMonth={cd.todayMonth} hoveredMonth={hovered} setHoveredMonth={setHovered} />
            </Card>
            <Card title="Cumulative billings — burn-up" sub="Portfolio billings climbing toward total contract value">
              <SCurveChart months={cd.rows} totalContract={cd.totalContract} todayMonth={cd.todayMonth} hoveredMonth={hovered} setHoveredMonth={setHovered} />
            </Card>
          </div>

          {/* 4 — sales pipeline (coming soon) */}
          <PipelineComingSoon />

          {/* 5 — per-project breakdown */}
          <Card title="Per-project breakdown" sub={breakdown === 'table' ? 'Every active project, ranked by projected billings' : 'Monthly projected billings — darker = more concentrated'}
            right={<Segmented active={breakdown} onChange={setBreakdown} size="sm" segs={[{ key: 'table', label: 'Table', icon: 'table' }, { key: 'heatmap', label: 'Heatmap', icon: 'grid' }]} />}>
            {breakdown === 'table'
              ? <ProjectTable rows={projectRows} allCount={cd.byProject.length} search={search} totalProjected={cd.totalProjected} onOpenProject={onOpenProject} />
              : projectRows.length === 0 && search.trim()
                ? <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</div>
                : <Heatmap rows={projectRows} axis={cd.axis} todayIdx={cd.todayIdx} onOpenProject={onOpenProject} />}
          </Card>
        </>
      )}
    </div>
  );
};

export default BillingsProjectionPage;
